import React from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Toaster } from "../ui/sonner";
import { Button } from "../ui/button";
import {
    LayoutDashboard,
    FolderKanban,
    FileText,
    Receipt,
    LogOut,
    Building2,
} from "lucide-react";
import { cn } from "../../lib/utils";

const portalNav = [
    { path: "/portal", label: "Tableau de bord", icon: LayoutDashboard, exact: true },
    { path: "/portal/projects", label: "Mes projets", icon: FolderKanban },
    { path: "/portal/quotes", label: "Mes devis", icon: FileText },
    { path: "/portal/invoices", label: "Mes factures", icon: Receipt },
];

export const PortalLayout = () => {
    const { user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate("/portal/auth");
    };

    const tenantName = user?.tenant_name || "Espace Client";

    return (
        <div className="min-h-screen bg-background" data-testid="portal-layout">
            {/* Top Header */}
            <header className="sticky top-0 z-50 bg-card border-b border-border">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
                                <Building2 className="w-5 h-5 text-primary-foreground" />
                            </div>
                            <div>
                                <h1 className="font-heading font-bold text-base text-foreground leading-none">
                                    {tenantName}
                                </h1>
                                <p className="text-xs text-muted-foreground">Espace Client</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <span className="text-sm text-muted-foreground hidden sm:block">
                                {user?.name || user?.email}
                            </span>
                            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="portal-logout-btn">
                                <LogOut className="w-4 h-4 mr-2" />
                                Déconnexion
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Nav Tabs */}
            <nav className="bg-card border-b border-border">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex gap-1 overflow-x-auto py-1">
                        {portalNav.map((item) => {
                            const isActive = item.exact
                                ? location.pathname === item.path
                                : location.pathname.startsWith(item.path);
                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    data-testid={`portal-nav-${item.path.split("/").pop() || "dashboard"}`}
                                    className={cn(
                                        "flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors whitespace-nowrap",
                                        isActive
                                            ? "bg-primary/10 text-primary"
                                            : "text-muted-foreground hover:text-foreground hover:bg-muted"
                                    )}
                                >
                                    <item.icon className="w-4 h-4" />
                                    {item.label}
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </nav>

            {/* Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <Outlet />
            </main>

            <Toaster position="top-right" richColors />
        </div>
    );
};
